<script setup lang="ts">
import { PropType } from 'vue'

/* Types */
import { SlugNews } from '@/types/api/api-v3/news'

/* Components */
import NewsCard from '@/components/News/NewsCard.vue'
const IconArrowLeft = defineAsyncComponent(
  () => import('@/components/Icons/IconArrowLeft.vue'),
)
const IconArrowRight = defineAsyncComponent(
  () => import('@/components/Icons/ArrowRight.vue'),
)

const emit = defineEmits(['update:model-value'])

defineProps({
  newsList: {
    type: Array as PropType<SlugNews[]>,
    required: true,
  },
  isLight: {
    type: Boolean,
    required: false,
  },
  countPages: {
    type: Number,
    required: true,
    default: 0,
  },
  currentPage: {
    type: Number,
    required: true,
    default: 1,
  },
  withImage: {
    type: Boolean,
    required: false,
  },
})

const totalVisible = 5

const onChangeCurrentPage = (value: number) => {
  emit('update:model-value', value)
}
</script>

<template>
  <div id="newsListContainer" class="news-list-container">
    <div class="news-list">
      <nuxt-link
        v-for="(news, index) in newsList"
        :key="index"
        :to="`/news/${news.slug}/`"
        :prefetch="false"
        class="news-list__news-link"
      >
        <news-card
          class="news-list__card"
          :news="news"
          :is-light="isLight"
          :with-image="withImage"
        />
      </nuxt-link>
    </div>
    <v-pagination
      v-if="newsList?.length"
      :value="currentPage"
      class="news-list__pagination"
      :class="{ 'news-list__pagination--light': isLight }"
      :length="countPages"
      :total-visible="totalVisible"
      :prev-icon="IconArrowLeft"
      :next-icon="IconArrowRight"
      @update:model-value="onChangeCurrentPage"
    />
  </div>
</template>

<style scoped lang="scss">
.news-list-container {
  padding-bottom: 120px;

  :deep {
    .v-pagination__list {
      display: flex;
      gap: 16px;
    }

    .v-pagination__item,
    .v-pagination__next,
    .v-pagination__prev {
      align-items: center;
      justify-content: center;
      min-width: 36px;
      height: 36px;
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      color: #fff;
      border-radius: 6px;
      box-shadow: none;

      .v-btn {
        align-items: center;
        justify-content: center;
        width: max-content;
        min-width: 36px;
        height: 36px;
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        color: #fff;
        background-color: $dark-grey;
        border-radius: 6px;
        box-shadow: none;
        opacity: 1;

        .v-btn__overlay {
          display: none;
        }
      }
    }

    .v-pagination__item--is-active {
      .v-btn {
        color: #fff;
        background-color: $primary;
      }
    }

    .v-pagination__next,
    .v-pagination__prev {
      .v-btn {
        width: 36px;
        height: 36px;

        svg {
          width: 12px;
          height: 12px;

          path {
            fill: #fff;
          }
        }

        &--disabled {
          background-color: #464b54;
          opacity: 0.6;

          svg path {
            fill: $dark-grey;
          }
        }
      }
    }

    .news-list__pagination--light {
      .v-pagination__item,
      .v-pagination__next,
      .v-pagination__prev {
        box-shadow: none;

        .v-btn {
          color: $primary;
          background-color: #fff3ee;
          box-shadow: none;
        }
      }

      .v-pagination__item--is-active {
        box-shadow: 0 4px 10px rgba(255, 80, 0, 0.16);

        .v-btn {
          color: #fff;
          background-color: $primary;
        }
      }

      .v-pagination__next,
      .v-pagination__prev {
        svg path {
          fill: $primary;
        }

        .v-btn {
          &--disabled {
            background-color: $button-background;
            svg path {
              fill: $carousel-dot-background;
            }
          }
        }
      }
    }
  }
}

.news-list {
  display: grid;
  grid-template-columns: repeat(
    3,
    calc((100% - 72px) / 3)
  ); // ширина карточки в 3 колонки с учетом column-gap: 36px;

  row-gap: 30px;
  column-gap: 36px;

  &__news-link {
    height: max-content;
    transition: 0.2s ease-in;

    &:hover {
      cursor: pointer;
      transform: translateY(5px);
    }
  }

  &__card {
    max-width: unset !important;
  }

  &__pagination {
    margin-top: 80px;
  }

  @include media('<desktop-mini') {
    grid-template-columns: repeat(
      2,
      calc((100% - 40px) / 2)
    ); // ширина карточки в 2 колонки с учетом gap: 20px;

    gap: 20px;

    &__pagination {
      margin-top: 60px;
    }
  }

  @include media('<tablet') {
    grid-template-columns: 100%;
  }
}
</style>
